import React, { useState } from 'react';
import { OperatorsHttpService } from '../../../../services/Http/OperatorsHttpService';
import {
    Button,
    Modal,
    Upload,
    Form,
    Typography,
    Collapse,
    CollapseProps,
    Table,
    TableProps,
    Badge,
    notification
} from 'antd';
import {FilterFilled, UploadOutlined} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import {IconButton} from "../../../Common/IconButton/IconButton";
import {IconLuxotticaDimension} from "../../../../models/IconLuxotticaDimension";
import {DiottrieTableData} from "../../../CambioVisus/DiottrieTable";

const { Paragraph } = Typography;

export interface LoadOperatorsProps {
    isOpen: boolean;
    onOk: () => void;
    onCancel: () => void;
    disabled?: boolean,
}

interface PreviewResponse{
    unchanged:any[],
    update:any[],
    add:any[],
    delete:any[],
    malformed:any[]
}
const DEFAULT_PREVIEW_RESPONSE_MAP = {
    add: [],
    delete: [],
    unchanged: [],
    update: [],
    malformed: [],
};

export function LoadOperatorsModal(props: LoadOperatorsProps) {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [fileUploading, setFileUploading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [responseMessage, setResponseMessage] = useState<string | null>(null);
    const [previewResponseMap, setPreviewResponseMap] = useState<PreviewResponse>(DEFAULT_PREVIEW_RESPONSE_MAP);

    const validMimeTypes = [
        'application/vnd.ms-excel', // .xls
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    ];

    const columns: TableProps<DiottrieTableData>['columns'] = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id'
        },
        {
            title: 'Tipo di negozio',
            dataIndex: 'locationGroupCode',
            key: 'locationGroupCode'
        },
        {
            title: 'Nome',
            dataIndex: 'name',
            key: 'name'
        },
        {
            title: 'Data di creazione',
            dataIndex: 'createdAt',
            key: 'createdAt' ,
            render: (timestamp) => {
                const date = new Date(timestamp * 1000);
                return date.toLocaleDateString();
            },
        },
        {
            title: 'Posizione del negozio',
            dataIndex: 'location',
            key: 'location'
        },
    ]

    const getTable= (data:any[], extraColumns:any[]=[])=>{
        return <Table columns={columns.concat(extraColumns)} dataSource={data} pagination={{  hideOnSinglePage: true }}>

        </Table>
    }

    const getCollapseExtra=(count:number)=>{
        return <Badge count={count} color={"#C8A2C8"} size="default" style={{ fontSize: '15px' }} showZero></Badge>;
    }


    const updatedItems: CollapseProps['items'] = [
        {
            key: '1',
            label: 'Caricati',
            children: getTable(previewResponseMap?.add),
            extra: getCollapseExtra(previewResponseMap?.add.length)
        },
        // {
        //     key: '2',
        //     label: 'Invariati',
        //     children: getTable(previewResponseMap?.UNCHANGED),
        //     extra: getCollapseExtra(previewResponseMap?.UNCHANGED.length)
        // },
        {
            key: '3',
            label: 'Aggiornati',
            children: getTable(previewResponseMap?.update),
            extra: getCollapseExtra(previewResponseMap?.update.length)
        },
        {
            key: '4',
            label: 'Disabilitati',
            children: getTable(previewResponseMap?.delete),
            extra: getCollapseExtra(previewResponseMap?.delete.length)
        },
        {
            key: '5',
            label: 'Malformate (Questi record non verranno salvati)',
            children: getTable(previewResponseMap?.malformed?.map((item)=>{
                return {
                    ...item.operator,
                    message: item.message
                };
            }), [{
                title: 'Motivo',
                dataIndex: 'message',
                key: 'message'
            }]),
            extra: getCollapseExtra(previewResponseMap?.malformed.length)
        }
    ];

    const handleFileChange = (info: any) => {
        const file = info.file;
        if (file && validMimeTypes.includes(file.type)) {
            setSelectedFile(file);
            setFileList([info.file]);
            setResponseMessage(null);
        } else {
            setSelectedFile(null);
            setFileList([]);
            setResponseMessage('Errore: Tipo di file non valido. Carica un file Excel (.xls o .xlsx).');
        }
    };

    const handleSaveAll = async () => {
        if(!responseMessage || !fileList || !selectedFile){
            setResponseMessage('Errore: Carica ed elabora un file prima di applicare le modifiche.');
            return;
        }
        const formData = new FormData();
        formData.append('file', selectedFile);
        setSaving(true);
        try {
            const response = await OperatorsHttpService.saveAllPracticeFiles(formData);
            console.log("response", response.status);
            if (response.status === 200) {
                setSelectedFile(null);
                setFileList([]);
                setPreviewResponseMap(DEFAULT_PREVIEW_RESPONSE_MAP)
                notification['success']({
                    message: 'Operazione completata con successo'
                });
            } else {
                throw new Error('Errore durante il salve');
            }
        } catch (error: any) {
            console.error('Errore durante il salve:', error);
            if (error.response.status === 400) {
                const data = error.response.data;
                setResponseMessage("Errore: " + data.message);
            } else {
                setResponseMessage('Errore durante il salve del file.');
            }
        } finally {
            setSaving(false);
        }
    }

    const handleFileUpload = async () => {
        if (!selectedFile) {
            setResponseMessage('Errore: Seleziona un file prima di caricare.');
            return;
        }
        const formData = new FormData();
        formData.append('file', selectedFile);
        setFileUploading(true);
        try {
            const response = await OperatorsHttpService.uploadPracticeFiles(formData);
            console.log("response", response.status);
            if (response.status === 200) {
                const data = response.data;
                setResponseMessage('Caricamento riuscito');
                setPreviewResponseMap(data.response.updatedMap)
            } else {
                throw new Error('Errore durante il caricamento');
            }
        } catch (error: any) {
            console.error('Errore durante il caricamento:', error);
            if (error.response.status === 400) {
                const data = error.response.data;
                setResponseMessage("Errore: " + data.message);
            } else {
                setResponseMessage('Errore durante il caricamento del file.');
            }
        } finally {
            setFileUploading(false);
        }
    };

    return <>
        <IconButton hide={props.disabled} icon={<FilterFilled/>} dimension={IconLuxotticaDimension.SMALL}></IconButton>
        <Modal open={props.isOpen}
               onCancel={props.onCancel}
               width={"80%"}
               footer={(_, {OkBtn, CancelBtn}) => (
                   <>
                       {responseMessage && selectedFile && (
                           <Button key="save" type="primary"
                                   onClick={handleSaveAll} loading={saving}  >
                               {saving ? 'Operazione in corso...' : 'Approva l\'operazione'}
                           </Button>
                       )}
                       <Form.Item style={{marginTop: '20px'}}>
                           <Upload multiple={false} beforeUpload={() => {
                               setSelectedFile(null);
                               setFileList([]);
                               return false;
                           }} onChange={handleFileChange} fileList={fileList} accept=".xls,.xlsx" >
                               <Button icon={<UploadOutlined />}>Seleziona file Excel</Button>
                           </Upload>
                       </Form.Item>
                       <Button key="close" onClick={props.onCancel}>
                           Chiudi
                       </Button>
                       <Button key="upload" type="primary"
                               onClick={handleFileUpload} loading={fileUploading} disabled={!selectedFile} >
                           {fileUploading ? 'Caricamento in corso...' : 'Carica'}
                       </Button>
                   </>
               )}
               modalRender={dom => (
                   <Form layout="vertical" className={"form-filter"}>
                       {dom}
                   </Form>
               )}
        >
            {responseMessage && (
                <>
                    <Paragraph style={{ color: responseMessage.includes('Errore') ? 'red' : 'green' }}>
                        {responseMessage}
                    </Paragraph>
                    {selectedFile && <Collapse items={updatedItems}  />}

                </>
            )}
        </Modal>
    </>
}
